<template>
  <label :class="[$style.label, disabled && $style.disabled]">
    <div :class="[$style.grid, noGap && $style.gapUnset, noPadding && $style.paddingUnset]">
      <div :class="$style.root">
        <button
          :class="[
            $style.toggler,
            {
              [$style.unchecked]: modelValue === false,
              [$style.checked]: modelValue === true,
              [$style.indeterminate]: modelValue === null,
            },
          ]"
          type="button"
          :disabled="disabled"
          @click="
            () => {
              model = !model;
            }
          "
        />
      </div>
      <span :class="[{ [$style.required]: required }]">{{ label }}</span>
    </div>
    <SCAHelperText v-if="helperText" :intent="alert ? 'alert' : undefined" :class="$style.helperText">
      {{ helperText }}
    </SCAHelperText>
  </label>
</template>

<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import type { Ref } from "vue";

import SCAHelperText from "./SCAHelperText.vue";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    modelValue?: boolean | null;
    alert?: boolean;
    label?: string;
    helperText?: string | Ref<string>;
    required?: boolean;
    noGap?: boolean;
    noPadding?: boolean;
  }>(),
  {
    disabled: false,
    modelValue: null,
    alert: false,
    label: undefined,
    helperText: undefined,
    required: false,
    noGap: false,
    noPadding: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": boolean | null;
}>();

const model = useVModel(props, "modelValue", emit, { passive: true });
</script>

<style lang="scss" module>
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.label {
  --toggler-size: 28px;
}

// A disabled toggle takes no pointer events at all, not just on its button: the label wraps the
// button, so a click anywhere on the label — its padding, its text — reaches a click listener a
// parent put on this component's root, which `disabled` on the button cannot stop (#3412). Callers
// change state from such a listener rather than from the model, which is what made a disabled
// toggle still flip when clicked next to (rather than on) the control.
//
// A tooltip explaining why a toggle is disabled keeps working: those are bound to a wrapper around
// this component, and the pointer passes straight through to it.
.disabled {
  pointer-events: none;
}

.grid {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  gap: var(--space-component-gap-small);
  padding: var(--space-component-gap-xSmall) 0;
}

.gapUnset {
  gap: unset;
}

.paddingUnset {
  padding: unset;
}

.helperText {
  padding-top: var(--space-component-gap-xSmall);
  padding-left: calc(var(--toggler-size) + var(--space-component-gap-small));
}

.root {
  flex: 0 0 var(--toggler-size);
  width: var(--toggler-size);
  height: var(--size-3xSmall);
  position: relative;

  .toggler {
    appearance: none;
    display: block;
    position: absolute;
    border-radius: var(--border-radius-full);
    inset: 0;

    &::after {
      content: "";
      width: var(--dimension-150);
      aspect-ratio: 1/1;
      border-radius: var(--border-radius-full);
      background: var(--color-interaction-inverted-normal);
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      transition: all 250ms;
    }

    &:focus {
      outline: none;
    }

    &:not([disabled]) {
      cursor: pointer;
    }

    &:disabled {
      opacity: 0.5;
    }

    &.unchecked {
      background: var(--color-content-disabled);
      &::after {
        left: 2px;
      }
      &:not([disabled]) {
        &:hover,
        &:focus {
          background: var(--color-interaction-disabled-hover);
        }
        &:active {
          background: var(--color-interaction-disabled-active);
        }
      }
    }

    &.checked,
    &.indeterminate {
      background: var(--color-interaction-default-normal);
      &:not([disabled]) {
        &:hover,
        &:focus {
          background: var(--color-interaction-default-hover);
        }
        &:active {
          background: var(--color-interaction-default-active);
        }
      }
    }

    &.checked {
      &::after {
        left: calc(100% - var(--dimension-150) - 2px);
      }
    }

    &.indeterminate {
      &::after {
        height: 1.5px;
        left: calc(50% - var(--dimension-150) / 2);
      }
    }
  }
}
</style>
